import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import MaterialLink from '@material-ui/core/Link';

import * as Store from '@apply/store';

import { Header } from './Header';
import { alertModal } from '@apply/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion } from '@fortawesome/free-regular-svg-icons';

export interface Actions {
    forgotPassword?: (email: string) => Promise<boolean>;
}

export interface ForgotPasswordPageProps {
    actions?: Actions;
}

export const ForgotPasswordPage: React.FC<ForgotPasswordPageProps> = ({ actions }) => {
    const { search } = useLocation();
    const queryParams = new URLSearchParams(search);
    const requestedEmail = queryParams.get('email');

    const [email, setEmail] = useState<string>(requestedEmail ?? '');
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [successful, setSuccessful] = useState<boolean>(false);

    const mounted = useRef<boolean>(true);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const handleRequestForgotPassword = async () => {
        /* istanbul ignore if */
        if (!email) {
            return;
        }

        setSubmitting(true);

        try {
            const result = await actions.forgotPassword(email);
            // istanbul ignore else
            if (mounted) {
                setSubmitting(false);
                if (result) {
                    setSuccessful(true);
                } else {
                    showError();
                }
            }
        } catch (err) {
            setSubmitting(false);
            setSuccessful(false);
            showError(err.message);
        }
    };

    const showError = (msg?: string | null | undefined) => {
        alertModal({
            message: msg ?? 'An error has occurred while communicating with our server.  Please try your operation again.',
        });
    };

    const onChangeEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value);
        setSubmitting(false);
    };

    if (successful) {
        return (
            <>
                <Header />

                <div className='app-container login apply-login-container'>
                    <h2>Your request has been processed</h2>
                    <main>
                        <div className='simple-form-container one-column'>Please check your email for the next steps...</div>
                    </main>
                </div>
            </>
        );
    }

    return (
        <>
            <Header />

            <div className='app-container login apply-login-container'>
                <h2>Request a Password Reset</h2>
                <h6 style={{ textAlign: 'center' }}>Please enter your email address.</h6>
                <h6 style={{ textAlign: 'center' }}>We will send you an email to reset your password.</h6>
                <main>
                    <div className='simple-form-container one-column'>
                        <div className='columns form-content'>
                            <div className='standard-form'>
                                <div className='row'>
                                    <div className='columns small-12 large-12'>
                                        <label htmlFor='emailAddress'>
                                            Email Address
                                            <span className='req'>*</span>
                                        </label>
                                        <input
                                            id='emailAddress'
                                            data-testid='forgot-password-email-address'
                                            type='email'
                                            autoFocus
                                            autoComplete='email'
                                            value={email}
                                            onChange={onChangeEmail}
                                            required
                                        />
                                        <span className='helper'>
                                            <span className='tooltip-trigger'>
                                                <FontAwesomeIcon icon={faCircleQuestion} />
                                            </span>
                                            <span className='text'>Enter your email address, i.e. johnsmith@email.com</span>
                                        </span>
                                    </div>
                                    <div className='columns small-12 large-12'>
                                        <button
                                            data-testid='forgot-password-button'
                                            disabled={!email || submitting}
                                            onClick={handleRequestForgotPassword}
                                            className='button signin-button'
                                        >
                                            Reset Password
                                        </button>
                                    </div>
                                    <div className='columns small-6 large-6'>
                                        <MaterialLink
                                            component={Link}
                                            to='/register'
                                        >
                                            Register
                                        </MaterialLink>
                                    </div>
                                    <div className='columns small-6 large-6 text-right'>
                                        <MaterialLink
                                            component={Link}
                                            to='/login'
                                        >
                                            Login
                                        </MaterialLink>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        </>
    );
};

export const mapDispatchToProps = (dispatch: Store.ApplyDispatchType) => {
    const props: { actions: Actions; dispatch: Store.ApplyDispatchType } = {
        actions: {
            forgotPassword: (email: string) => dispatch(Store.CrossStore.forgotPassword(email)),
        },
        dispatch,
    };
    return props;
};

export default connect(null, mapDispatchToProps)(ForgotPasswordPage);
